import type { Composer } from 'vue-i18n';
import { translateString } from './translate-string';

export interface TranslateObjectOptions<K, P extends boolean> {
	keys?: readonly K[];
	i18n: Composer;

	/**
	 * Only return an object containing the passed keys and not the full object
	 */
	pick?: P;
}

export type TranslatableRecord = Record<PropertyKey, any> | undefined | null;
// Get the keys of T while ignoring `null` or `undefined`
export type SafeKeyOf<T> = keyof Exclude<T, undefined | null>;
export type SafePick<T, K extends keyof Exclude<T, undefined | null>> = Pick<Exclude<T, undefined | null>, K> | Extract<T, undefined | null>;

export function translateObject<
	T extends TranslatableRecord,
	K extends SafeKeyOf<T>,
>(object: T, options: TranslateObjectOptions<K, false>): T;

export function translateObject<
	T extends TranslatableRecord,
	K extends SafeKeyOf<T>,
>(object: T, options: TranslateObjectOptions<K, true>): SafePick<T, K>;

export function translateObject<
	T extends TranslatableRecord,
	K extends SafeKeyOf<T>,
	P extends boolean,
	TReturn = P extends true ? SafePick<T, K> : T,
>(
	object: T,
	options: TranslateObjectOptions<keyof TReturn, P>,
): TReturn {
	if (!object) return object as unknown as TReturn;

	const {
		keys = Object.keys(object) as (keyof TReturn)[] ?? [],
		i18n,
		pick = false,
	} = options;

	if (keys.length > 0) {
		const result: TReturn = (pick ? {} : { ...object }) as TReturn;

		for (const key of keys) {
			if (Object.prototype.hasOwnProperty.call(object, key) && typeof object[key] === 'string') {
				result[key] = translateString(object[key], i18n);
			}
		}

		return result;
	}

	return object;
}
